import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ScreenSelectionModalShell, {
  ScreenSelectionItem,
} from './ScreenSelectionModalShell';
import {
  AndroidDisplayInfo,
  getAllDisplays,
  getCurrentDisplayId,
  moveToDisplay,
  requestDrawOverlays,
} from '../../../utils/secondaryDisplay';

interface Props {
  onClose: () => void;
}

const AndroidScreenSelectionModal: React.FC<Props> = ({ onClose }) => {
  const [displays, setDisplays] = useState<AndroidDisplayInfo[]>([]);
  const [savedDisplayId, setSavedDisplayId] = useState<number>(-1);
  const [loading, setLoading] = useState(true);
  const [isMoving, setIsMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const [allDisplays, currentId] = await Promise.all([
          getAllDisplays(),
          getCurrentDisplayId(),
        ]);
        if (mounted) {
          setDisplays(allDisplays);
          setSavedDisplayId(currentId);
        }
      } catch (e) {
        if (mounted) {
          setError('Failed to load displays. Please try again.');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const handleSelect = useCallback(
    async (displayId: number) => {
      if (isMoving) return;
      setIsMoving(true);
      setError(null);
      try {
        await moveToDisplay(displayId);
        onClose();
      } catch (e) {
        const nativeError = e as { message?: string; code?: string };
        if (nativeError?.code === 'OVERLAY_PERMISSION_REQUIRED') {
          setError(
            'Permission required: Go to Settings → Apps → CDS → Display over other apps and enable it, then try again.',
          );
          try {
            await requestDrawOverlays();
          } catch {
            // Settings screen failed to open — error message is already shown
          }
        } else {
          setError(
            nativeError?.message ?? 'Failed to move to selected display.',
          );
        }
        setIsMoving(false);
      }
    },
    [isMoving, onClose],
  );

  const items = useMemo<ScreenSelectionItem[]>(
    () =>
      displays.map(display => ({
        key: display.displayId,
        displayName: display.name,
        width: display.width,
        height: display.height,
        isPrimary: display.isPrimary,
        isCurrent: display.isCurrent,
        isStarred: display.displayId === savedDisplayId && !display.isCurrent,
        starredBadgeLabel: 'Saved',
        onSelect: () => handleSelect(display.displayId),
      })),
    [displays, savedDisplayId, handleSelect],
  );

  return (
    <ScreenSelectionModalShell
      items={items}
      loading={loading}
      isMoving={isMoving}
      error={error}
      onClose={onClose}
    />
  );
};

export default AndroidScreenSelectionModal;
